/**
 * De iconen van het scherm.
 *
 * Hier stonden emoji (📌 ☀️ ☕). Die brengen hun eigen kleuren mee, zien er op elk
 * platform anders uit, en de brandguide schrijft iets anders voor: getekende
 * lijniconen met "een speels karakter om een vriendelijke uitstraling te hebben"
 * (guide p. 16). Vandaar deze drie — lijnen met ronde uiteinden, bewust niet
 * kaarsrecht, in één kleur per icoon die via `currentColor` uit de CSS komt.
 *
 * Alle drie op een 24×24-raster met dezelfde lijndikte, zodat ze naast elkaar even
 * zwaar staan.
 */
type Naam = 'speld' | 'zon' | 'koffie';

const PADEN: Record<Naam, React.ReactNode> = {
  // Speld: het merkteken bij een wijziging die de hele dag raakt. De omtrek loopt
  // bewust niet helemaal rond en de lijnen sluiten niet naadloos — dat is het
  // getekende karakter dat de guide vraagt, en niet een geometrische pin.
  speld: (
    <>
      <path d="M12.2 20.4c-3.4-4.3-5.2-7.3-5.3-9.1-.2-3 2.1-5.6 5.1-5.8 3-.2 5.6 2 5.8 5 .1 1.9-1.6 5-5.1 9.5" />
      <path d="M12.1 8.6c-1.2 0-2.2 1-2.2 2.2 0 1.3 1 2.3 2.3 2.3 1.2 0 2.2-1 2.2-2.3 0-1.1-.9-2.1-2-2.2" />
      <path d="M19.2 4.9c.5-.5 1.1-.8 1.8-1M4.9 4.6c-.6-.4-1.2-.6-1.9-.7" />
    </>
  ),
  // Zon voor de grote pauze: de bol iets ovaal, de stralen ongelijk van lengte.
  zon: (
    <>
      <path d="M12 7.6c2.4-.1 4.5 1.9 4.5 4.3 0 2.4-2 4.4-4.4 4.3-2.4 0-4.4-2-4.3-4.4 0-2.2 1.8-4.1 4-4.2" />
      <path d="M12 3.2v2.1M12 18.6v2.3M4.7 12h2.2M17.2 12h2.3" />
      <path d="M6.6 6.5l1.6 1.5M15.9 15.8l1.6 1.7M17.4 6.6l-1.5 1.6M8.2 15.9l-1.7 1.6" />
    </>
  ),
  // Koffie voor de korte pauze: kopje met oor en twee slingertjes stoom.
  koffie: (
    <>
      <path d="M5.6 9.4h10.1c.2 2.4.1 4.8-1.3 6.5-.8 1-2 1.5-3.4 1.5-1.7 0-3-.6-3.8-1.8-1.2-1.7-1.4-4-1.6-6.2" />
      <path d="M15.8 10.6c1.3-.3 2.4.3 2.5 1.5.1 1.2-.9 2.1-2.3 2" />
      <path d="M4.3 19.6c2.9.5 8.9.5 11.9-.1" />
      <path d="M9.4 6.8c.6-.6.6-1.4 0-2.1M12.6 6.9c.7-.7.6-1.6-.1-2.4" />
    </>
  ),
};

export function Icoon({ naam, className = 'icon' }: { naam: Naam; className?: string }) {
  return (
    <span className={className} aria-hidden="true">
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        xmlns="http://www.w3.org/2000/svg"
      >
        {PADEN[naam]}
      </svg>
    </span>
  );
}
